<script setup lang="ts">
/**
 * Resolution type/detail selector — custom dropdowns matching prototype design.
 * Parses ticket title into type + detail, emits updated title on change.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useDropdownGroup } from 'src/composables/useDropdownState';
import SharedDropdown from 'src/components/shared/SharedDropdown.vue';
import { RESOLUTION_TYPES, type ResolutionType, type ResolutionDetail } from './resolution-data';

const props = defineProps<{
  title?: string;
}>();

const emit = defineEmits<{
  'update:title': [title: string];
}>();

const rootEl = ref<HTMLElement>();
const { active: activeDropdown, toggle: toggleDropdown } = useDropdownGroup(rootEl);

const selectedType = ref<ResolutionType | null>(null);
const selectedDetail = ref<ResolutionDetail | null>(null);

const hasDetails = computed(() => (selectedType.value?.details.length ?? 0) > 0);

function parseTitle(title: string): void {
  if (!title) return;
  const parts = title.split(' - ');
  const typeName = parts[0]?.toLowerCase().trim();
  const detailName = parts[1]?.toLowerCase().trim();

  const foundType = RESOLUTION_TYPES.find((t) => t.name.toLowerCase() === typeName);
  if (!foundType) return;

  selectedType.value = foundType;
  if (detailName && foundType.details.length > 0) {
    selectedDetail.value = foundType.details.find((d) => d.name.toLowerCase() === detailName) ?? null;
  }
}

function selectType(type: ResolutionType): void {
  selectedType.value = type;
  selectedDetail.value = null;
  activeDropdown.value = null;
  emitTitle();
}

function selectDetail(detail: ResolutionDetail): void {
  selectedDetail.value = detail;
  activeDropdown.value = null;
  emitTitle();
}

function emitTitle(): void {
  if (!selectedType.value) return;
  const title = selectedDetail.value
    ? `${selectedType.value.name} - ${selectedDetail.value.name}`
    : selectedType.value.name;
  emit('update:title', title);
}

watch(() => props.title, (t) => { if (t) parseTitle(t); }, { immediate: true });

onMounted(() => {
  if (props.title) parseTitle(props.title);
});
</script>

<template>
  <div ref="rootEl" class="flex flex-col gap-2">
    <!-- Resolution Type -->
    <div class="relative flex-1">
      <button class="field-select w-full" @click="toggleDropdown('type')">
        <span class="text-sm">{{ selectedType?.name || 'Select type' }}</span>
        <i class="pi pi-chevron-down field-select-chevron" />
      </button>
      <SharedDropdown
        :active="activeDropdown" name="type"
        :items="RESOLUTION_TYPES" :active-id="selectedType?.id"
        max-height="240px" @select="selectType"
      />
    </div>

    <!-- Resolution Detail -->
    <div class="relative flex-1">
      <button
        class="field-select w-full"
        :class="{ 'opacity-40 pointer-events-none': !hasDetails }"
        @click="toggleDropdown('detail')"
      >
        <span class="text-sm">{{ selectedDetail?.name || '—' }}</span>
        <i class="pi pi-chevron-down field-select-chevron" />
      </button>
      <SharedDropdown
        v-if="hasDetails"
        :active="activeDropdown" name="detail"
        :items="selectedType?.details ?? []" :active-id="selectedDetail?.id"
        max-height="240px" @select="selectDetail"
      />
    </div>
  </div>
</template>

<style scoped>
.field-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-radius: 0.625rem;
  border: 1px solid var(--p-surface-200);
  background: white;
  color: var(--p-surface-800);
  cursor: pointer;
  transition: all 0.15s;
}
:root[class*='app-dark'] .field-select {
  border-color: var(--p-surface-700);
  background: var(--p-surface-800);
  color: var(--p-surface-200);
}
.field-select:hover {
  border-color: var(--p-primary-300);
}
:root[class*='app-dark'] .field-select:hover {
  border-color: var(--p-primary-600);
}
.field-select-chevron {
  font-size: 10px;
  color: var(--p-surface-400);
}

:deep(.te-dropdown-item) {
  font-size: 0.8125rem;
}
</style>
